import { Loading } from "../../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  computeQuote,
  formatEuro,
  formatIban,
  quoteDisplayStatus,
  quoteTitle,
  SALES_MODULES,
  SALES_QUOTE_STATUS_LABELS,
  sellerMissing,
  type SalesAgreementDto,
  type SalesCustomerRow,
  type SalesModuleKey,
  type SalesQuoteDetail,
  type SalesQuoteInput,
  type SalesQuoteRow,
  type SalesQuoteStatus,
  type SalesSettings,
} from "@klantpunt/shared";
import { IconChevronRight, IconDownload, IconPlus, IconTrash } from "../../app/icons";
import { Meta } from "../../app/Meta";
import { Modal } from "../../app/Modal";
import { PageHeader } from "../../app/PageHeader";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { PlatformGuard } from "../../pages/Platform";
import { PlatformHeader } from "./PlatformHeader";
import { QuoteSummary } from "./QuoteSummary";

const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");
export const quotesKey = ["platform", "sales", "quotes"] as const;
const quoteKey = (id: string) => ["platform", "sales", "quote", id] as const;
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtDay = (iso: string | null) =>
  iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })
    : "";

const fmtMoment = (iso: string) => new Date(iso).toLocaleString("nl-NL", { dateStyle: "long", timeStyle: "short" });

type ShownStatus = SalesQuoteStatus | "expired";
const STATUS_TONE: Record<ShownStatus, string> = {
  draft: "",
  sent: "tone tone-info",
  accepted: "tone tone-ok",
  declined: "tone tone-danger",
  expired: "tone tone-warn",
};

export function QuoteStatusChip({ status }: { status: ShownStatus }) {
  return <span className={`chip ${STATUS_TONE[status]}`}>{SALES_QUOTE_STATUS_LABELS[status]}</span>;
}

export function SalesQuotesPage() {
  return (
    <PlatformGuard>
      <SalesQuotes />
    </PlatformGuard>
  );
}

/** Platformbeheer › Offertes: alle offertes aan verkoopklanten (docs/VERKOOP.md, stap 2). */
function SalesQuotes() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: quotesKey, queryFn: () => api<SalesQuoteRow[]>("/platform/sales/quotes") });
  const [filter, setFilter] = useState<ShownStatus | null>(null);
  const [creating, setCreating] = useState(false);
  const today = todayIso();
  const all = useMemo(
    () => (q.data ?? []).map((r) => ({ ...r, shown: quoteDisplayStatus(r.status, r.validUntil, today) })),
    [q.data, today],
  );
  const rows = filter ? all.filter((r) => r.shown === filter) : all;
  const statuses: ShownStatus[] = ["draft", "sent", "accepted", "declined", "expired"];

  return (
    <div className="page platform sales-page">
      <PlatformHeader
        title="Offertes"
        action={
          <button type="button" className="btn primary" onClick={() => setCreating(true)}>
            <IconPlus size={20} aria-hidden="true" />
            Offerte
          </button>
        }
      />
      <div className="dir-filters">
        <div className="dir-kinds" role="group" aria-label="Status">
          <button type="button" className="kpi" aria-pressed={filter === null} onClick={() => setFilter(null)}>
            Alle <span className="count num">{all.length}</span>
          </button>
          {statuses.map((s) => {
            const n = all.filter((r) => r.shown === s).length;
            if (!n && s === "expired") return null;
            return (
              <button
                key={s}
                type="button"
                className="kpi"
                aria-pressed={filter === s}
                onClick={() => setFilter(filter === s ? null : s)}
              >
                {SALES_QUOTE_STATUS_LABELS[s]} <span className="count num">{n}</span>
              </button>
            );
          })}
        </div>
      </div>
      <section className="panel tscroll" aria-busy={q.isPending}>
        {q.isPending ? (
          <Loading />
        ) : rows.length ? (
          <table className="it sales-table" aria-label="Offertes">
            <thead>
              <tr>
                <th scope="col">Offerte</th>
                <th scope="col" className="dir-hide-narrow">
                  Status
                </th>
                <th scope="col" className="num">
                  Per maand
                </th>
                <th scope="col" className="dir-hide-narrow">
                  Geldig tot
                </th>
                <th scope="col" className="dir-hide-narrow">
                  <span className="sr-only">Openen</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="co-click" onClick={() => void navigate(`/platform/offertes/${r.id}`)}>
                  <td className="tcell-title">
                    <Link
                      to={`/platform/offertes/${r.id}`}
                      className="co-rowtitle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {r.customerName}
                    </Link>
                    <Meta className="small muted co-block" items={[r.number, r.title]} />
                    <span className="sales-stage-narrow">
                      <QuoteStatusChip status={r.shown} />
                    </span>
                  </td>
                  <td className="dir-hide-narrow">
                    <QuoteStatusChip status={r.shown} />
                  </td>
                  <td className="num">{formatEuro(r.monthly)}</td>
                  <td className="dir-hide-narrow sales-date">{fmtDay(r.validUntil)}</td>
                  <td className="dir-hide-narrow">
                    <IconChevronRight size={18} aria-hidden="true" className="muted" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">
            {all.length
              ? "Geen offertes met deze status."
              : "Nog geen offertes. Maak er een voor een klant; aantallen en contactpersoon komen van de klant."}
          </p>
        )}
      </section>
      {creating && <NewQuoteDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/** Nieuwe offerte: kies de klant (of kom vanaf de klantpagina met de klant al gekozen). */
export function NewQuoteDialog({ customerId, onClose }: { customerId?: string; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const customers = useQuery({
    queryKey: ["platform", "sales", "customers"],
    queryFn: () => api<SalesCustomerRow[]>("/platform/sales/customers"),
    enabled: !customerId,
  });
  const [chosen, setChosen] = useState(customerId ?? "");
  const create = useMutation({
    mutationFn: (id: string) =>
      api<SalesQuoteDetail>("/platform/sales/quotes", { method: "POST", body: { customerId: id } }),
    onSuccess: async (d) => {
      qc.setQueryData(quoteKey(d.id), d);
      await qc.invalidateQueries({ queryKey: ["platform", "sales"] });
      onClose();
      void navigate(`/platform/offertes/${d.id}`);
    },
    onError: (e) => toast(errMsg(e)),
  });
  // Vanaf de klantpagina: meteen maken, geen keuze nodig.
  const started = useRef(false);
  useEffect(() => {
    if (customerId && !started.current) {
      started.current = true;
      create.mutate(customerId);
    }
  }, [customerId, create]);
  if (customerId) return null;

  const options = (customers.data ?? []).filter((c) => c.stage !== "lost");
  return (
    <Modal
      title="Nieuwe offerte"
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!chosen || create.isPending}
            onClick={() => create.mutate(chosen)}
          >
            {create.isPending ? "Bezig…" : "Offerte maken"}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Klant *</span>
        <select value={chosen} onChange={(e) => setChosen(e.target.value)} autoFocus>
          <option value="">Kies een klant</option>
          {options.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.city ? ` (${c.city})` : ""}
            </option>
          ))}
        </select>
        <span className="hint">
          Aantal gebouwen, opdrachtgevers en de contactpersoon komen van de klant. Staat de klant er niet bij?{" "}
          <Link to="/platform/klanten">Voeg hem toe bij Klanten</Link>.
        </span>
      </label>
    </Modal>
  );
}

export function SalesQuotePage() {
  return (
    <PlatformGuard>
      <SalesQuote />
    </PlatformGuard>
  );
}

type SaveState = "idle" | "saving" | "saved" | "error";

function SalesQuote() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: quoteKey(id), queryFn: () => api<SalesQuoteDetail>(`/platform/sales/quotes/${id}`) });
  const settings = useQuery({
    queryKey: ["platform", "sales", "settings"],
    queryFn: () => api<SalesSettings>("/platform/sales/settings"),
  });
  const [input, setInput] = useState<SalesQuoteInput | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const dirty = useRef(false);

  useEffect(() => {
    if (q.data && !dirty.current) setInput(q.data.input);
  }, [q.data]);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<SalesQuoteDetail>(`/platform/sales/quotes/${id}`, { method: "PATCH", body }),
    onSuccess: async (d) => {
      qc.setQueryData(quoteKey(id), d);
      await qc.invalidateQueries({ queryKey: quotesKey });
    },
  });

  // Automatisch opslaan, kort na de laatste wijziging.
  useEffect(() => {
    if (!input || !dirty.current) return;
    const t = setTimeout(() => {
      dirty.current = false;
      setSaveState("saving");
      patch.mutate(
        { input },
        {
          onSuccess: () => setSaveState("saved"),
          onError: (e) => {
            setSaveState("error");
            toast(errMsg(e));
          },
        },
      );
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij een nieuwe invoer
  }, [input]);

  const del = useMutation({
    mutationFn: () => api(`/platform/sales/quotes/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["platform", "sales"] });
      toast("Offerte verwijderd");
      void navigate("/platform/offertes");
    },
    onError: (e) => toast(errMsg(e)),
  });

  if (q.isPending || q.isError || !input)
    return (
      <div className="center" aria-busy={!q.isError}>
        {q.isError ? <p className="muted">{errMsg(q.error)}</p> : <Loading />}
      </div>
    );
  const d = q.data;
  const draft = d.status === "draft";
  const totals = computeQuote(input, d.prices);
  const shown = quoteDisplayStatus(d.status, d.validUntil, todayIso());
  const pricesOutdated =
    draft && settings.data && Object.entries(settings.data.prices).some(([k, v]) => d.prices[k as never] !== v);
  const missing = sellerMissing(d.seller);

  const change = (p: Partial<SalesQuoteInput>) => {
    dirty.current = true;
    setSaveState("idle");
    setInput((v) => (v ? { ...v, ...p } : v));
  };
  const setStatus = (status: SalesQuoteStatus, msg: string) =>
    patch.mutate({ status }, { onSuccess: () => toast(msg), onError: (e) => toast(errMsg(e)) });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/offerte/${d.token}`);
      toast("Link gekopieerd");
    } catch {
      toast(`Kopiëren lukte niet: ${window.location.origin}/offerte/${d.token}`);
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { downloadQuotePdf } = await import("./quotePdf");
      await downloadQuotePdf({ ...d, input });
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="page platform co-page sales-page">
      <PageHeader
        label={<Link to="/platform/offertes">Offertes</Link>}
        title={d.customer.name}
        action={
          <button type="button" className="btn primary" onClick={() => void downloadPdf()} disabled={pdfBusy}>
            <IconDownload size={20} aria-hidden="true" />
            {pdfBusy ? "PDF maken…" : "PDF"}
          </button>
        }
        menu={
          <>
            {draft && (
              <button type="button" className="menuitem" onClick={() => setStatus("sent", "Gemarkeerd als verstuurd")}>
                Markeren als verstuurd
              </button>
            )}
            {d.status === "sent" && (
              <>
                <button type="button" className="menuitem" onClick={() => setStatus("accepted", "Akkoord vastgelegd")}>
                  Akkoord ontvangen
                </button>
                <button
                  type="button"
                  className="menuitem"
                  onClick={() => setStatus("declined", "Afgewezen vastgelegd")}
                >
                  Afgewezen
                </button>
              </>
            )}
            <a className="menuitem" href={`/offerte/${d.token}`} target="_blank" rel="noopener">
              {draft ? "Voorbeeld akkoordpagina" : "Akkoordpagina openen"}
            </a>
            {!draft && (
              <button type="button" className="menuitem" onClick={() => void copyLink()}>
                Link voor de klant kopiëren
              </button>
            )}
            {d.status !== "draft" && d.status !== "accepted" && (
              <button type="button" className="menuitem" onClick={() => setStatus("draft", "Terug naar concept")}>
                Terug naar concept
              </button>
            )}
            <Link className="menuitem" to={`/platform/klanten/${d.customer.id}`}>
              Naar de klant
            </Link>
            {draft && (
              <button type="button" className="menuitem danger" onClick={() => setConfirmDelete(true)}>
                <IconTrash size={18} aria-hidden="true" /> Verwijderen
              </button>
            )}
          </>
        }
      />
      <div className="co-sub">
        <QuoteStatusChip status={shown} />
        <Meta
          className="muted small"
          items={[
            d.number,
            d.version > 1 && `versie ${d.version}`,
            d.validUntil && `geldig tot ${fmtDay(d.validUntil)}`,
            d.status === "sent" &&
              (d.viewedAt ? `geopend door de klant op ${fmtDay(d.viewedAt.slice(0, 10))}` : "nog niet geopend"),
          ]}
        />
        <span className="muted small" role="status" aria-live="polite">
          {saveState === "saving" ? "Opslaan…" : saveState === "saved" ? "Opgeslagen" : ""}
        </span>
      </div>
      {d.agreement ? (
        <p className="alert ok">
          Online akkoord door {d.agreement.signerName} op {fmtMoment(d.agreement.acceptedAt)}. Deze offerte is nu de
          overeenkomst en ligt vast.
        </p>
      ) : (
        !draft && (
          <p className="alert info">
            Deze offerte is {SALES_QUOTE_STATUS_LABELS[d.status].toLowerCase()} en ligt vast. Wil je iets wijzigen? Zet
            hem via ⋯ terug naar concept; de klant tekent dan een nieuwe versie.
          </p>
        )
      )}
      {pricesOutdated && (
        <p className="alert info">
          De prijslijst is gewijzigd sinds deze offerte is gemaakt.{" "}
          <button
            type="button"
            className="linkbtn inline"
            onClick={() =>
              patch.mutate(
                { refreshPrices: true },
                { onSuccess: () => toast("Prijzen bijgewerkt"), onError: (e) => toast(errMsg(e)) },
              )
            }
          >
            Nieuwe prijzen gebruiken
          </button>
        </p>
      )}
      {draft && missing.length > 0 && (
        <p className="alert info">
          Verkopergegevens nog niet compleet ({missing.join(", ")}). De PDF kun je al maken als voorbeeld, versturen en
          online tekenen kan pas als ze compleet zijn;{" "}
          <Link to="/platform/facturatie?tab=verkoper">vul ze aan bij Facturatie</Link>.
        </p>
      )}

      <div className="co-grid quote-grid">
        <div className="co-col">
          <QuoteForm d={d} input={input} disabled={!draft} change={change} />
        </div>
        <div className="co-col">
          {d.agreement && <AgreementPanel a={d.agreement} />}
          <QuoteSummary totals={totals} input={input} />
        </div>
      </div>

      {confirmDelete && (
        <Modal
          title="Offerte verwijderen"
          onClose={() => setConfirmDelete(false)}
          busy={del.isPending}
          footer={
            <>
              <span className="grow" />
              <button type="button" className="btn ghost" onClick={() => setConfirmDelete(false)}>
                Annuleren
              </button>
              <button type="button" className="btn danger" disabled={del.isPending} onClick={() => del.mutate()}>
                Verwijderen
              </button>
            </>
          }
        >
          <p>
            Concept {d.number} voor {d.customer.name} verwijderen? Het nummer wordt niet opnieuw gebruikt.
          </p>
        </Modal>
      )}
    </div>
  );
}

const intOf = (v: string) => Math.max(0, Math.round(Number(v.replace(",", ".")) || 0));

function QuoteForm({
  d,
  input,
  disabled,
  change,
}: {
  d: SalesQuoteDetail;
  input: SalesQuoteInput;
  disabled: boolean;
  change: (p: Partial<SalesQuoteInput>) => void;
}) {
  const modCount = (k: SalesModuleKey) => input.modules.find((m) => m.key === k)?.buildings ?? 0;
  const setMod = (k: SalesModuleKey, buildings: number) =>
    change({
      modules: [...input.modules.filter((m) => m.key !== k), ...(buildings > 0 ? [{ key: k, buildings }] : [])],
    });
  return (
    <fieldset className="quote-form" disabled={disabled}>
      <section className="panel co-panel" aria-label="Voorstel">
        <h2 className="label">Voorstel</h2>
        <div className="formgrid">
          <label className="field">
            <span>Voor</span>
            <select value={input.contactId ?? ""} onChange={(e) => change({ contactId: e.target.value || null })}>
              <option value="">Geen contactpersoon</option>
              {d.contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.role ? `, ${c.role}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Titel op de voorkant</span>
            <input
              value={input.title}
              placeholder={quoteTitle({ title: "", buildings: input.buildings })}
              onChange={(e) => change({ title: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Ingangsdatum</span>
            <input
              type="date"
              value={input.startOn ?? ""}
              onChange={(e) => change({ startOn: e.target.value || null })}
            />
          </label>
          <label className="field">
            <span>Geldig tot</span>
            <input
              type="date"
              value={input.validUntil ?? ""}
              onChange={(e) => change({ validUntil: e.target.value || null })}
            />
          </label>
          <label className="field">
            <span>Looptijd (maanden)</span>
            <input
              inputMode="numeric"
              value={String(input.termMonths)}
              onChange={(e) => change({ termMonths: Math.max(1, intOf(e.target.value)) })}
            />
          </label>
        </div>
        <label className="field">
          <span>Persoonlijke inleiding</span>
          <textarea
            rows={3}
            value={input.intro}
            placeholder="Leeg laten voor de standaardtekst op de samenvatting"
            onChange={(e) => change({ intro: e.target.value })}
          />
        </label>
      </section>

      <section className="panel co-panel" aria-label="Omvang">
        <h2 className="label">Omvang</h2>
        <div className="formgrid">
          <label className="field">
            <span>Gebouwen</span>
            <input
              inputMode="numeric"
              value={String(input.buildings)}
              onChange={(e) => change({ buildings: Math.max(1, intOf(e.target.value)) })}
            />
          </label>
          <label className="field">
            <span>Opdrachtgevers met eigen omgeving</span>
            <input
              inputMode="numeric"
              value={String(input.clients)}
              onChange={(e) => change({ clients: intOf(e.target.value) })}
            />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={input.allIn} onChange={(e) => change({ allIn: e.target.checked })} />
          All-in: basis en alle modules voor {formatEuro(d.prices.allin)} per gebouw
        </label>
      </section>

      {!input.allIn && (
        <section className="panel co-panel" aria-label="Modules">
          <h2 className="label">Modules</h2>
          <p className="muted small">Per module het aantal gebouwen. De basis zit altijd in het voorstel.</p>
          <ul className="quote-mods">
            {SALES_MODULES.map((m) => {
              const n = modCount(m.key);
              return (
                <li key={m.key}>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={n > 0}
                      onChange={(e) => setMod(m.key, e.target.checked ? input.buildings : 0)}
                    />
                    <span>
                      <b>{m.label}</b>
                      <span className="small muted co-block">
                        {formatEuro(d.prices[m.key])} per gebouw · {m.description}
                      </span>
                    </span>
                  </label>
                  {n > 0 && (
                    <label className="quote-modcount">
                      <span className="sr-only">Gebouwen met {m.label}</span>
                      <input
                        inputMode="numeric"
                        value={String(n)}
                        onChange={(e) => setMod(m.key, Math.min(intOf(e.target.value), input.buildings) || 1)}
                      />
                      <span className="small muted">van {input.buildings}</span>
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="panel co-panel" aria-label="Korting en voorwaarden">
        <h2 className="label">Korting en voorwaarden</h2>
        <div className="formgrid">
          <label className="field">
            <span>Korting (%)</span>
            <input
              inputMode="decimal"
              value={String(input.discountPct).replace(".", ",")}
              onChange={(e) =>
                change({ discountPct: Math.min(100, Math.max(0, Number(e.target.value.replace(",", ".")) || 0)) })
              }
            />
          </label>
          <label className="field">
            <span>Korting geldt (maanden, 0 = altijd)</span>
            <input
              inputMode="numeric"
              value={String(input.discountMonths)}
              onChange={(e) => change({ discountMonths: intOf(e.target.value) })}
            />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={input.setup} onChange={(e) => change({ setup: e.target.checked })} />
          Inrichting en import ({formatEuro(d.prices.setup)} eenmalig)
        </label>
        {input.setup && (
          <label className="check">
            <input
              type="checkbox"
              checked={input.setupWaived}
              onChange={(e) => change({ setupWaived: e.target.checked })}
            />
            Inrichting kosteloos
          </label>
        )}
        <label className="check">
          <input type="checkbox" checked={input.allowNoPo} onChange={(e) => change({ allowNoPo: e.target.checked })} />
          Klant mag akkoord geven zonder PO-nummer
        </label>
      </section>
    </fieldset>
  );
}

/** In de samenvatting alleen toelichting die iets zegt over deze offerte (korting, deel van de gebouwen). */

/** Het online akkoord: de gegevens voor de overeenkomst en de facturatie (stap 4). */
function AgreementPanel({ a }: { a: SalesAgreementDto }) {
  return (
    <section className="panel co-panel" aria-labelledby="quote-agreement">
      <h2 id="quote-agreement">Akkoord</h2>
      <dl className="kv">
        <dt>Getekend door</dt>
        <dd>
          {a.signerName}
          {a.signerRole && <span className="muted"> · {a.signerRole}</span>}
          <span className="small muted co-block">{a.signerEmail}</span>
        </dd>
        <dt>Tijdstip</dt>
        <dd>
          {fmtMoment(a.acceptedAt)}
          <span className="small muted co-block">
            Versie {a.version}
            {a.ip && ` · IP ${a.ip}`}
          </span>
        </dd>
        <dt>{a.noPo ? "Kenmerk" : "PO-nummer"}</dt>
        <dd className="num">{a.noPo ? a.reference || "Zonder PO" : a.poNumber}</dd>
        {a.poBudget !== null && (
          <>
            <dt>PO-budget</dt>
            <dd className="num">
              {formatEuro(a.poBudget)}
              {a.poUntil && <span className="muted"> tot {fmtDay(a.poUntil)}</span>}
            </dd>
          </>
        )}
        {a.poBudget === null && a.poUntil && (
          <>
            <dt>PO geldig tot</dt>
            <dd>{fmtDay(a.poUntil)}</dd>
          </>
        )}
        <dt>Rekening</dt>
        <dd className="num">
          {formatIban(a.iban)}
          {a.ibanHolder && <span className="small muted co-block">{a.ibanHolder}</span>}
        </dd>
        <dt>Facturen naar</dt>
        <dd>{a.invoiceEmail || a.signerEmail}</dd>
        {a.billingAddress && (
          <>
            <dt>Factuuradres</dt>
            <dd className="prewrap">{a.billingAddress}</dd>
          </>
        )}
      </dl>
    </section>
  );
}
